import React, { useState, useEffect, useRef, useCallback } from 'react';
import Loading from '../../Common/Loading';
import CustomAlert from '../../Common/CustomAlert';
import popup_bg1 from '../../Common/Images/ImagesForAlert/popup_bg1.png';
import alert_close_n from '../../Common/Images/ImagesForAlert/alert_close_n.png';
import alert_close_o from '../../Common/Images/ImagesForAlert/alert_close_o.png';
import alert_A_bg from '../../Common/Images/ImagesForAlert/alert_A_bg.png';
import ok_n from '../../Common/Images/ImagesForAlert/ok_n.png';
import ok_o from '../../Common/Images/ImagesForAlert/ok_o.png';

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

interface Props {
    mode: 'A' | 'R';
    storeID?: number | null;
    onClose: () => void;
    onSaveSuccess: () => void;
    showWarning: (msg: string) => void;
    showConfirm: (msg: string, onConfirm: () => void) => void;
}

interface Dealer {
    Name: string;
    SchoolCode: string;
    Country: string;
}

// ─── Local CustomConfirm ────────────────────────────────────────────────────
interface CustomConfirmProps {
    message: string;
    onYes: () => void;
    onNo: () => void;
}

const CustomConfirm: React.FC<CustomConfirmProps> = ({ message, onYes, onNo }) => {
    const [isYesHovered, setIsYesHovered] = useState(false);
    const [isNoHovered, setIsNoHovered] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [offset, setOffset] = useState({ x: 0, y: 0 });

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        setIsDragging(true);
        setOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
    };

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (isDragging) {
            setPosition({ x: e.clientX - offset.x, y: e.clientY - offset.y });
        }
    }, [isDragging, offset]);

    const handleMouseUp = () => setIsDragging(false);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove]);

    const btnStyle: React.CSSProperties = {
        width: '70px', height: '28px', margin: '0 8px',
        cursor: 'pointer', fontFamily: 'Tahoma', fontSize: '13px',
        fontWeight: 'bold', border: '1px solid #888',
        borderRadius: '3px', backgroundColor: '#e0e0e0'
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(204,204,204,0.5)', zIndex: 10001,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            pointerEvents: isDragging ? 'all' : 'auto'
        }}>
            <div style={{
                width: '506px', height: '246px',
                backgroundImage: `url(${alert_A_bg})`,
                backgroundRepeat: 'no-repeat',
                position: 'relative', fontFamily: 'Tahoma',
                fontSize: '14px', color: 'white',
                transform: `translate(${position.x}px, ${position.y}px)`,
                userSelect: 'none'
            }}>
                {/* Drag handle / title bar */}
                <div
                    onMouseDown={handleMouseDown}
                    style={{
                        width: '100%', height: '30px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 'bold', cursor: 'move', position: 'relative'
                    }}
                >
                    Confirm
                    <img
                        src={alert_close_n} alt="Close"
                        onClick={onNo}
                        onMouseDown={(e) => e.stopPropagation()}
                        style={{
                            position: 'absolute', right: '15px', top: '50%',
                            transform: 'translateY(-50%)', cursor: 'pointer'
                        }}
                    />
                </div>

                {/* Message area */}
                <div style={{
                    height: '145px', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', padding: '0 20px',
                    color: 'black', overflowY: 'auto', textAlign: 'center'
                }}>
                    <div dangerouslySetInnerHTML={{ __html: message }} />
                </div>

                {/* Yes / No buttons */}
                <div style={{
                    height: '70px', display: 'flex',
                    alignItems: 'center', justifyContent: 'center'
                }}>
                    <button
                        style={{
                            ...btnStyle,
                            backgroundColor: isYesHovered ? '#f6861f' : '#f6861f',
                            color:"white",
                            border:"none",
                            fontWeight:"normal",
                            boxShadow:'1px 1px 2px rgba(0, 0, 0, 0.3)',
                            borderRadius: '8px',
                            textShadow:'1px 1px 2px rgba(0, 0, 0, 0.3)'
                        }}
                        onMouseEnter={() => setIsYesHovered(true)}
                        onMouseLeave={() => setIsYesHovered(false)}
                        onClick={onYes}
                    >
                        Yes
                    </button>
                    <button
                        style={{
                            ...btnStyle,
                            backgroundColor: isNoHovered ? '#f6861f' : '#f6861f',
                            color:"white",
                            border:"none",
                            fontWeight:"normal",
                            boxShadow:'1px 1px 2px rgba(0, 0, 0, 0.3)',
                            borderRadius: '8px',
                            textShadow:'1px 1px 2px rgba(0, 0, 0, 0.3)'
                        }}
                        onMouseEnter={() => setIsNoHovered(true)}
                        onMouseLeave={() => setIsNoHovered(false)}
                        onClick={onNo}
                    >
                        No
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Main Component ─────────────────────────────────────────────────────────
const EL_StoresARD: React.FC<Props> = ({ mode, storeID, onClose, onSaveSuccess, showWarning, showConfirm }) => {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [itemType, setItemType] = useState<'G' | 'I' | 'D'>('G');
    const [title, setTitle] = useState('');
    const [price, setPrice] = useState('');
    const [donationAmount, setDonationAmount] = useState('');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0]);
    const [maxNumber, setMaxNumber] = useState('');
    const [maxPerStudent, setMaxPerStudent] = useState('');
    const [description, setDescription] = useState('');
    const [terms, setTerms] = useState('');
    const [selectedImage, setSelectedImage] = useState('');

    const [previewUrl, setPreviewUrl] = useState('');

    const [availableImages, setAvailableImages] = useState<string[]>([]);
    const [countries, setCountries] = useState<string[]>([]);
    const [dealers, setDealers] = useState<Dealer[]>([]);

    const [chkSchools, setChkSchools] = useState(false);
    const [chkHomes, setChkHomes] = useState(false);
    const [alertMsg, setAlertMsg] = useState('');
    const [selectedSchoolCountries, setSelectedSchoolCountries] = useState<string[]>([]);
    const [selectedHomeCountries, setSelectedHomeCountries] = useState<string[]>([]);
    const [selectedDealers, setSelectedDealers] = useState<{Country: string, SchoolCode: string}[]>([]);

    // Local confirm dialog state
    const [confirmState, setConfirmState] = useState<{ visible: boolean; message: string; onYes: () => void } | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        loadInitialData();
    }, []);

    useEffect(() => {
        if (selectedImage) {
            fetchSasUrl(selectedImage);
        } else {
            setPreviewUrl('');
        }
    }, [selectedImage]);

    useEffect(() => {
        if (selectedHomeCountries.length > 0) {
            fetchDealers(selectedHomeCountries);
        } else {
            setDealers([]);
        }
    }, [selectedHomeCountries]);

    const fetchSasUrl = async (imgName: string) => {
        try {
            const res = await fetch(`${API_BASE_URL}api/manage-store/get_image_url/?image_name=${imgName}`);
            if (res.ok) {
                const data = await res.json();
                setPreviewUrl(data.url);
            } else {
                setPreviewUrl('');
            }
        } catch (e) {
            console.error("Error fetching image URL:", e);
            setPreviewUrl('');
        }
    };

    const loadInitialData = async () => {
        setLoading(true);
        try {
            const resC = await fetch(`${API_BASE_URL}api/manage-store/countries/`);
            if (resC.ok) setCountries(await resC.json());

            const resImg = await fetch(`${API_BASE_URL}api/manage-store/images/`);
            if (resImg.ok) setAvailableImages(await resImg.json());

            if (mode === 'R' && storeID) {
                const resD = await fetch(`${API_BASE_URL}api/manage-store/${storeID}/`);
                if (resD.ok) {
                    const data = await resD.json();
                    const store = data.store;
                    const alloc = data.allocations;

                    setTitle(store.StoreTitle);
                    setItemType(store.ItemType);
                    setPrice(store.Price);
                    if (store.DonationAmount) setDonationAmount(store.DonationAmount);
                    if (store.StartDate) setStartDate(store.StartDate.split('T')[0]);
                    if (store.EndDate) setEndDate(store.EndDate.split('T')[0]);
                    if (store.MaxNumber) setMaxNumber(store.MaxNumber);
                    if (store.MaxPerStudent) setMaxPerStudent(store.MaxPerStudent);
                    if (store.Image) setSelectedImage(store.Image.replace('.png', '').replace('.jpg', ''));
                    setDescription(store.Description || '');
                    setTerms(store.Terms || '');

                    if (store.AllocatedTo === 'S' || store.AllocatedTo === 'A') setChkSchools(true);
                    if (store.AllocatedTo === 'F' || store.AllocatedTo === 'A') setChkHomes(true);

                    setSelectedSchoolCountries(alloc.SchoolCountries || []);

                    const uniqueHomeCountries = Array.from(new Set(alloc.HomeDealers.map((d: any) => d.Country))) as string[];
                    setSelectedHomeCountries(uniqueHomeCountries);
                    setSelectedDealers(alloc.HomeDealers || []);
                }
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const fetchDealers = async (countryList: string[]) => {
        setLoading(true);
        let allDealers: Dealer[] = [];
        for (const c of countryList) {
            const res = await fetch(`${API_BASE_URL}api/manage-comps/dealers/?country=${c}`);
            if (res.ok) {
                const data = await res.json();
                const mapped = data.map((d: any) => ({ ...d, Country: c }));
                allDealers = [...allDealers, ...mapped];
            }
        }
        setDealers(allDealers);
        setLoading(false);
    };

    const handleSave = async () => {
        if (!title.trim()) { showWarning("Please enter a title"); return; }
        if (!price) { showWarning("Please enter the price"); return; }
        if (!selectedImage) { showWarning("Please select an image"); return; }
        if (!description.trim()) { showWarning("Please enter a description"); return; }
        if (!terms.trim()) { showWarning("Please enter terms and conditions"); return; }

        if (itemType === 'I' || itemType === 'D') {
            if (!maxNumber) { showWarning("Please enter Max. number"); return; }
            if (!maxPerStudent) { showWarning("Please enter Max. per student"); return; }
            if (parseInt(maxNumber) < parseInt(maxPerStudent)) {
                showWarning("Max. number cannot be less than Max. per student"); return;
            }
        }

        if (description.length > 100) { showWarning("Description length can not be more than 100."); return; }
        if (terms.length > 1000) { showWarning("'Terms & Condition' length can not be more than 1000."); return; }

        if ((chkSchools && selectedSchoolCountries.length === 0) && (chkHomes && selectedDealers.length === 0)) { showWarning("Please select Allocated to"); return; }
        if (!chkSchools && !chkHomes) { showWarning("Please select Allocated To"); return; }

        const payload = {
            Title: title,
            Type: itemType,
            Price: price,
            DonationAmount: itemType === 'D' ? donationAmount : null,
            StartDate: startDate,
            EndDate: endDate,
            MaxNumber: (itemType === 'I' || itemType === 'D') ? maxNumber : null,
            MaxPerStudent: (itemType === 'I' || itemType === 'D') ? maxPerStudent : null,
            Image: selectedImage + ".png",
            Description: description,
            Terms: terms,
            AllocatedSchools: chkSchools ? selectedSchoolCountries : [],
            AllocatedHomes: chkHomes ? selectedDealers : []
        };

        const message = mode === 'A' ? 'Do you want to add a new store?' : 'Do you want to save changes?';

        showConfirm(message, async () => {
            setLoading(true);
            try {
                const url = mode === 'A'
                    ? `${API_BASE_URL}api/manage-store/`
                    : `${API_BASE_URL}api/manage-store/${storeID}/`;

                const method = mode === 'A' ? 'POST' : 'PUT';

                const res = await fetch(url, {
                    method: method,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload)
                });

                if (res.ok) {
                    onSaveSuccess();
                    onClose();
                } else {
                    const err = await res.json();
                    showWarning(err.error || "Error saving store.");
                }
            } catch (e) {
                showWarning("Network Error");
            } finally {
                setLoading(false);
            }
        });
    };

    // ── Fixed: always show local CustomConfirm regardless of mode ──
    const handleCancel = () => {
        setConfirmState({
            visible: true,
            message: 'Do you want to close the window?',
            onYes: onClose
        });
    };

    const handleUpload = async () => {
        if (fileInputRef.current?.files && fileInputRef.current.files.length > 0) {
            const file = fileInputRef.current.files[0];
            const formData = new FormData();
            formData.append('file', file);

            setUploading(true);
            try {
                const res = await fetch(`${API_BASE_URL}api/manage-store/upload_image/`, {
                    method: 'POST',
                    body: formData
                });
                if (res.ok) {
                    setAlertMsg('Image uploaded successfully.');
                    const resImg = await fetch(`${API_BASE_URL}api/manage-store/images/`);
                    if (resImg.ok) setAvailableImages(await resImg.json());
                    setSelectedImage(file.name.replace(/\.[^/.]+$/, ""));
                } else {
                    setAlertMsg('Upload failed.');
                }
            } catch (e) {
                setAlertMsg('Error uploading file.');
            } finally {
                setUploading(false);
            }
        }
    };

    const toggleSelection = (list: any[], item: any, setList: any, key?: string) => {
        if (key) {
            const exists = list.some(x => x.SchoolCode === item.SchoolCode && x.Country === item.Country);
            if (exists) {
                setList(list.filter((x: any) => !(x.SchoolCode === item.SchoolCode && x.Country === item.Country)));
            } else {
                setList([...list, item]);
            }
        } else {
            if (list.includes(item)) {
                setList(list.filter((x: any) => x !== item));
            } else {
                setList([...list, item]);
            }
        }
    };

    const styles = {
        overlay: {
            position: 'fixed' as const, top: 0, left: 0, width: '100%', height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center'
        },
        modal: {
            backgroundColor: '#f0ffff', width: '100%', height: '85%', paddingBottom: '150px',
            display: 'flex', flexDirection: 'column' as const, border: 'none',
            fontFamily: 'Arial', fontSize: '11pt'
        },
        popupHeader: {
            backgroundImage: `url(${popup_bg1})`,
            backgroundRepeat: 'repeat-x',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            borderBottom: '1px solid #000', height: '30px',
            color: 'white', fontFamily: 'Calibri, Arial, sans-serif',
            fontSize: '11pt', fontWeight: 'bold' as const, paddingLeft: '10px'
        },
        maroonHeader: {
            backgroundColor: 'Maroon', color: 'White', padding: '5px',
            fontSize: '12pt', fontFamily: 'Arial', fontWeight: 'bold' as const,
            textAlign: 'center' as const, marginBottom: '5px'
        },
        mainContainer: {
            display: 'flex', flex: 1, margin: '0px 10px',border: '1px solid #82a0f6', overflow: 'hidden', 
        },
        leftCol: {
            width: '50%', paddingRight: '10px', borderRight: '1px solid #82a0f6', backgroundColor: '#f0ffff',
            display: 'flex', flexDirection: 'column' as const, overflowY: 'auto' as const
        },
        rightCol: {
            width: '50%', paddingLeft: '10px',
            display: 'flex', flexDirection: 'column' as const,
            minHeight: 0,
            overflow: 'hidden'
        },
        formRow: {
            display: 'flex', alignItems: 'center', marginBottom: '8px', minHeight: '30px'
        },
        labelCol: {
            width: '150px', fontWeight: 'bold' as const, fontSize: '10pt', fontFamily: 'Arial'
        },
        inputCol: {
            flex: 1
        },
        sectionHeader: {
            borderBottom: '1px solid #80A0FF', backgroundColor: '#C7DDFD',
            fontSize: '11pt', fontWeight: 'bold' as const, padding: '5px', marginBottom: '10px'
        },
        input: {
            border: '1px solid #82A0F6', padding: '2px', fontFamily: 'Arial', fontSize: '11pt', width: '200px'
        },
        textArea: {
            border: '1px solid #82A0F6', padding: '2px', fontFamily: 'Arial', fontSize: '11pt', width: '98%', height: '70px', resize: 'none' as const
        },
        imagePreview: {
            border: '1px solid #82A0F6', height: '120px', width: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '5px'
        },
        allocationHeader: {
            backgroundColor: '#ECFACB', padding: '5px', marginBottom: '5px',
            borderBottom: '1px solid #82A0F6', fontSize: '10pt', fontWeight: 'bold' as const
        },
        scrollList: {
            flex: 1, overflowY: 'auto' as const, overflowX: 'hidden' as const,
            border: '1px solid #82A0F6', width: '100%', marginBottom: '10px', backgroundColor: '#f0ffff'
        },
        gridHeader: {
            backgroundColor: '#c7ddfd', padding: '5px', fontWeight: 'bold' as const,
            borderBottom: '1px solid #82A0F6', fontSize: '11pt'
        },
        footer: {
            padding: '10px', textAlign: 'center' as const, 
            backgroundColor: '#f0ffff'
        },
        btn: {
            width: '100px', height: '30px', margin: '0 5px', cursor: 'pointer',
            fontFamily: 'Arial', fontSize: '10pt'
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>

                <div style={styles.popupHeader}>
                    <span style={{ fontSize: '20px'}}>&nbsp;Add Edit Stores</span>
                    <img
                        src={alert_close_n} alt="Close" onClick={onClose}
                        style={{ cursor: 'pointer' }}
                        onMouseOver={(e) => e.currentTarget.src = alert_close_o}
                        onMouseOut={(e) => e.currentTarget.src = alert_close_n}
                    />
                </div>

                <div style={styles.maroonHeader}>
                    {mode === 'A' ? 'Add New Store' : 'Modify Store'}
                </div>

                {loading && <Loading />}

                <div style={styles.mainContainer}>

                    <div style={styles.leftCol}>
                        <div style={styles.sectionHeader}>
                            {mode === 'A' ? 'Add New Store Item' : 'View/Modify Store Item'}
                        </div>

                        <div style={styles.formRow}>
                            <div style={styles.labelCol}>Type</div>
                            <div style={styles.inputCol}>
                                <label style={{ marginRight: '15px' }}><input type="radio" style={{ marginTop:'-3px' }} checked={itemType === 'G'} onChange={() => setItemType('G')} /> Unlocked game</label>
                                <label style={{ marginRight: '15px' }}><input type="radio" style={{ marginTop:'-3px' }} checked={itemType === 'I'} onChange={() => setItemType('I')} /> Item</label>
                                <label style={{ marginTop:'-2px' }}><input type="radio" style={{ marginTop:'-3px' }} checked={itemType === 'D'} onChange={() => setItemType('D')} /> Donation</label>
                            </div>
                        </div>

                        <div style={styles.formRow}>
                            <div style={styles.labelCol}>Title (Max 50)</div>
                            <div style={styles.inputCol}>
                                <input style={styles.input} maxLength={50} value={title} onChange={(e) => setTitle(e.target.value)} />
                            </div>
                        </div>

                        <div style={styles.formRow}>
                            <div style={styles.labelCol}>Price (Max 999999)</div>
                            <div style={styles.inputCol}>
                                <input type="number" style={styles.input} maxLength={9} value={price} onChange={(e) => setPrice(e.target.value)} />
                            </div>
                        </div>

                        {itemType === 'D' && (
                            <div style={styles.formRow}>
                                <div style={styles.labelCol}>Donation Amount</div>
                                <div style={styles.inputCol}>
                                    <input type="number" style={styles.input} maxLength={9} value={donationAmount} onChange={(e) => setDonationAmount(e.target.value)} />
                                </div>
                            </div>
                        )}

                        <div style={styles.formRow}>
                            <div style={styles.labelCol}>Start Date (GMT)</div>
                            <div style={styles.inputCol}>
                                <input type="date" style={styles.input} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                            </div>
                        </div>

                        <div style={styles.formRow}>
                            <div style={styles.labelCol}>End Date (GMT)</div>
                            <div style={styles.inputCol}>
                                <input type="date" style={styles.input} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                            </div>
                        </div>

                        {(itemType === 'I' || itemType === 'D') && (
                            <>
                                <div style={styles.formRow}>
                                    <div style={styles.labelCol}>Max. number</div>
                                    <div style={styles.inputCol}>
                                        <input type="number" style={styles.input} maxLength={5} value={maxNumber} onChange={(e) => setMaxNumber(e.target.value)} />
                                    </div>
                                </div>
                                <div style={styles.formRow}>
                                    <div style={styles.labelCol}>Max. per student</div>
                                    <div style={styles.inputCol}>
                                        <input type="number" style={styles.input} maxLength={5} value={maxPerStudent} onChange={(e) => setMaxPerStudent(e.target.value)} />
                                    </div>
                                </div>
                            </>
                        )}

                        <div style={styles.formRow}>
                            <div style={styles.labelCol}>Thumb Image</div>
                            <div style={styles.inputCol}>
                                <select style={{ ...styles.input, width: '200px' }} value={selectedImage} onChange={(e) => setSelectedImage(e.target.value)}>
                                    <option value="">-- select --</option>
                                    {availableImages.map(img => (
                                        <option key={img} value={img}>{img}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div style={{ ...styles.formRow, alignItems: 'flex-start' }}>
                            <div style={styles.labelCol}></div>
                            <div style={styles.inputCol}>
                                <div style={{...styles.imagePreview, height:'200px', width: '280px', padding:'5px'}} >
                                    {previewUrl ? (
                                        <img src={previewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'fill', }} />
                                    ) : (
                                        <span style={{ color: '#999' }}>{selectedImage ? 'Loading...' : 'No Image'}</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div style={{ ...styles.formRow, alignItems: 'flex-start', marginTop: '10px' }}>
                            <div style={styles.labelCol}>Description</div>
                            <div style={styles.inputCol}>
                                <textarea style={styles.textArea} maxLength={100} value={description} onChange={(e) => setDescription(e.target.value)} />
                            </div>
                        </div>

                        <div style={{ ...styles.formRow, alignItems: 'flex-start' }}>
                            <div style={styles.labelCol}>Terms & Conditions</div>
                            <div style={styles.inputCol}>
                                <textarea style={styles.textArea} maxLength={1000} value={terms} onChange={(e) => setTerms(e.target.value)} />
                            </div>
                        </div>
                    </div>

                    <div style={styles.rightCol}>

                        <div style={{ display: 'flex', flexDirection: 'column', flex: -1, marginBottom: '10px' }}>
                            <div style={styles.allocationHeader}>
                                <label>
                                    <input type="checkbox" checked={chkSchools} onChange={(e) => setChkSchools(e.target.checked)} />
                                    &nbsp;Allocated to schools in
                                </label>
                            </div>
                            {/* <div style={styles.scrollList}>
                                <table width="100%" cellSpacing={0} cellPadding={2}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#c7ddfd', fontWeight: 'bold' }}></tr>
                                    </thead>
                                    <tbody>
                                        {countries.map(c => (
                                            <tr key={c} style={{ backgroundColor: selectedSchoolCountries.includes(c) ? '#FFFFCC' : 'transparent' }}>
                                                <td width="30px">
                                                    <input
                                                        type="checkbox"
                                                        disabled={!chkSchools}
                                                        checked={selectedSchoolCountries.includes(c)}
                                                        onChange={() => toggleSelection(selectedSchoolCountries, c, setSelectedSchoolCountries)}
                                                    />
                                                </td>
                                                <td>{c}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div> */}
                            <div style={{ width: '100%', marginRight: '5px',  display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                                <div style={{ ...styles.scrollList, marginBottom: 0, flex: 1, overflowY: 'auto', backgroundColor: '#f0ffff', minHeight: 0 }}>
                                    <table width="100%" cellSpacing={0} cellPadding={2}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#c7ddfd', fontWeight: 'bold' }}>
                                                <td style={{ position: 'sticky', top: 0, zIndex: 2, background: '#c7ddfd', borderRight: '1px solid #82A0F6' }} width="30px" />
                                                <td style={{ position: 'sticky', top: 0, zIndex: 2, background: '#c7ddfd', borderRight: '1px solid #82A0F6' }}>Country</td>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {countries.map((c, i) => (
                                                <tr key={c} style={{ backgroundColor: selectedSchoolCountries.includes(c) ? '#FFFFCC' : (i % 2 === 0 ? '#F4FAFF' : 'white') }}>
                                                    <td width="30px" style={{  borderRight: '1px solid #82A0F6' }}>
                                                        <input
                                                            type="checkbox"
                                                            disabled={!chkSchools}
                                                            checked={selectedSchoolCountries.includes(c)}
                                                            onChange={() => toggleSelection(selectedSchoolCountries, c, setSelectedSchoolCountries)}
                                                        />
                                                    </td>
                                                    <td style={{  borderRight: '1px solid #82A0F6' }}>{c}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        {/* <hr style={{ width: '103%', borderTop: '1px solid #82A0F6', margin: '0 0 10px 0' }} /> */}

                        <div style={{ display: 'flex', flexDirection: 'column', height: '300px', overflow: 'hidden', minHeight: 0 }}>
                            <div style={styles.allocationHeader}>
                                <label>
                                    <input type="checkbox" checked={chkHomes} onChange={(e) => setChkHomes(e.target.checked)} />
                                    &nbsp;Allocated to home users
                                </label>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
                                <div style={{ width: '98%', marginBottom: '10px',  display: 'flex', flexDirection: 'column', minHeight: 0, height: '110px' }}>
                                    <div style={{ ...styles.scrollList, marginBottom: 0, flex: 1, overflowY: 'auto', backgroundColor: '#f0ffff', minHeight: 0 }}>
                                        <table width="100%" cellSpacing={0} cellPadding={2}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#c7ddfd', fontWeight: 'bold' }}>
                                                    <td style={{ position: 'sticky', top: 0, zIndex: 2, background: '#c7ddfd',  borderRight: '1px solid #82A0F6' }} width="30px" />
                                                    <td style={{ position: 'sticky', top: 0, zIndex: 2, background: '#c7ddfd',  borderRight: '1px solid #82A0F6' }}>Country</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {countries.map((c, i) => (
                                                    <tr key={c} style={{ backgroundColor: selectedHomeCountries.includes(c) ? '#FFFFCC' : (i % 2 === 0 ? '#F4FAFF' : 'white') }}>
                                                        <td width="30px" style={{  borderRight: '1px solid #82A0F6' }}>
                                                            <input
                                                                type="checkbox"
                                                                disabled={!chkHomes}
                                                                checked={selectedHomeCountries.includes(c)}
                                                                onChange={() => toggleSelection(selectedHomeCountries, c, setSelectedHomeCountries)}
                                                            />
                                                        </td>
                                                        <td style={{  borderRight: '1px solid #82A0F6' }}>{c}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div style={{ width: '98%',  display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
                                    <div style={{ ...styles.scrollList, marginBottom: 0, flex: 1, overflowY: 'auto', backgroundColor: '#f0ffff', minHeight: 0 }}>
                                        <table width="100%" cellSpacing={0} cellPadding={2}>
                                            <thead>
                                                <tr style={{ backgroundColor: '#c7ddfd', fontWeight: 'bold' }}>
                                                    <td style={{ position: 'sticky', top: 0, zIndex: 2, background: '#c7ddfd',  borderRight: '1px solid #82A0F6' }} width="30px" />
                                                    <td style={{ position: 'sticky', top: 0, zIndex: 2, background: '#c7ddfd',  borderRight: '1px solid #82A0F6' }}>eDealer</td>
                                                    <td style={{ position: 'sticky', top: 0, zIndex: 2, background: '#c7ddfd',  borderRight: '1px solid #82A0F6' }} width="80px">Country</td>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {dealers.map((d, i) => (
                                                    <tr key={`${d.SchoolCode}-${i}`} style={{ backgroundColor: i % 2 === 0 ? '#F4FAFF' : 'white' }}>
                                                        <td style={{ width: '30px',  borderRight: '1px solid #82A0F6' }}>
                                                            <input
                                                                type="checkbox"
                                                                disabled={!chkHomes}
                                                                checked={selectedDealers.some(x => x.SchoolCode === d.SchoolCode && x.Country === d.Country)}
                                                                onChange={() => toggleSelection(selectedDealers, { Country: d.Country, SchoolCode: d.SchoolCode }, setSelectedDealers, 'obj')}
                                                            />
                                                        </td>
                                                        <td style={{  borderRight: '1px solid #82A0F6' }}>{d.Name}</td>
                                                        <td style={{ width: '80px',  borderRight: '1px solid #82A0F6' }}>{d.Country}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                <div style={styles.footer}>

                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '10px' }}>
                        <button style={styles.btn} onClick={handleSave}>Save</button>
                        <button style={styles.btn} onClick={handleCancel}>Cancel</button>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '10px' }}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            style={{ border: '1px solid #5B91B5', width: '365px', fontFamily: 'Arial', fontSize: '10pt' }}
                        />
                        <button style={styles.btn} onClick={handleUpload} disabled={uploading}>
                            {uploading ? 'Uploading...' : 'Upload'}
                        </button>
                    </div>
                </div>

            </div>

            {/* Local confirm dialog for Cancel button */}
            {confirmState?.visible && (
                <CustomConfirm
                    message={confirmState.message}
                    onYes={() => {
                        setConfirmState(null);
                        confirmState.onYes();
                    }}
                    onNo={() => setConfirmState(null)}
                />
            )}

            {alertMsg && (
                <CustomAlert
                    message={alertMsg}
                    onClose={() => setAlertMsg('')}
                />
            )}
        </div>
    );
};

export default EL_StoresARD;